/* Structure tree (DESIGN5 §FEUX1, DESIGN9 §1–2) — a collapsible outline
   of the poster spec. Each row maps 1:1 to a data-path in the rendered
   SVG, so:
     click  → onSelect(path)  (EditorView also zooms + flashes the canvas)
     hover  → onHover(path)   (canvas glows the matching group)
   The tree reads the mutable working copy (workingRef.current) directly
   and re-renders on formRev, so structural edits (add/move/remove) show
   up immediately. Open/closed state lives in a ref keyed by path so a
   formRev bump never collapses what the operator expanded.

   DESIGN9: `selPath` here is the *reveal target* (the last-selected path,
   kept alive by EditorView even after the form closes) so returning to
   the tree via '◀ Structure' scrolls that row into view + pulses it once
   (rule 1). A pinned search box filters to a FLAT result list with
   breadcrumb sublines and <mark> highlights (rule 2); its query lives in
   EditorView so it survives this component's remounts. */

import React, { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import {
  AlignLeft, ChevronDown, ChevronRight, ChevronsDownUp, ChevronsUpDown,
  Columns2, Diamond, Flag, GitFork, Heading, Layers, Rows3, Search,
  Square, Table2, X,
} from 'lucide-react';
import { CommentBlockButton, commentBlockAttributes } from './CommentAnchors';
import type { CommentAnchor } from './api';
import { Kind, PALETTE, pathKind, resolvePath, nodeTitlePreview } from './utils';
import './structure-tree.css';

interface Props {
  workingRef: React.MutableRefObject<any>;
  formRev: number;
  // The reveal target: the currently/last selected path. Drives the
  // selected-row highlight + scroll-into-view + pulse.
  selPath: string | null;
  onSelect: (path: string) => void;
  onHover: (path: string | null) => void;
  // Search query — lifted to EditorView so it survives remounts.
  query: string;
  setQuery: (q: string) => void;
  commentAnchorForPath?: (path: string) => CommentAnchor | null;
}

interface RowSpec {
  path: string;
  depth: number;
  kind: Kind;
  label: string;
  preview?: string;
  colorToken?: string;
  nodeType?: string;
  badge?: string;
  hasChildren?: boolean;
}

/** A flat search hit: a row plus its precomputed haystack + breadcrumb. */
interface SearchEntry extends RowSpec {
  hay: string;
  crumb: string;
}

/* ── shared helpers (also consumed by the EditorView breadcrumb) ── */

/** One clickable crumb: a meaningful ancestor of the selected path. */
export interface CrumbSeg {
  path: string;
  kind: Kind;
  label: string;
}

/** Last numeric segment of a path — the index of the element it names. */
function lastIndex(path: string): number {
  const segs = path.split('.');
  for (let i = segs.length - 1; i >= 0; i--) {
    if (/^\d+$/.test(segs[i])) return parseInt(segs[i], 10);
  }
  return 0;
}

/** Short human label for a path segment, e.g. 'Case 1', 'Lane 3A',
    'Node [3A-1]', 'Row 2'. Used by both the tree and the breadcrumb. */
export function segLabel(kind: Kind, value: any, path: string): string {
  switch (kind) {
    case 'meta':
      return 'Poster header';
    case 'legend':
      return 'Legend';
    case 'block':
      return 'Legend block';
    case 'case':
      return 'Case ' + (lastIndex(path) + 1);
    case 'final':
      return 'Final block';
    case 'node':
      return 'Node [' + ((value && value.id) || lastIndex(path) + 1) + ']';
    case 'lane':
      return 'Lane ' + ((value && value.tag) || lastIndex(path) + 1);
    case 'table':
      return 'Table';
    case 'tableRow':
      return 'Row ' + (lastIndex(path) + 1);
    default:
      return 'Element';
  }
}

/** Build the breadcrumb: every ancestor prefix of `path` that names a
    real element (meta / case / lane / node / final / table / row / …). */
export function crumbSegments(spec: any, path: string): CrumbSeg[] {
  const segs = path.split('.');
  const out: CrumbSeg[] = [];
  let prefix = '';
  for (let i = 0; i < segs.length; i++) {
    prefix = i === 0 ? segs[i] : prefix + '.' + segs[i];
    const kind = pathKind(prefix);
    if (!kind) continue;
    const r = resolvePath(spec, prefix);
    out.push({ path: prefix, kind, label: segLabel(kind, r ? r.value : undefined, prefix) });
  }
  return out;
}

/** One-line static description shown under the breadcrumb per kind. */
export const KIND_DESC: Record<Kind, string> = {
  meta: 'The poster title, subtitle and footer.',
  legend: 'The legend: user, dashboard, AI nucleus, case chips and bullet blocks.',
  block: 'A bullet block in the legend right column.',
  case: 'A case group: intro, pre-lane nodes, parallel lanes and the final block.',
  final: 'The closing block under the lanes: title, paragraphs, list and tail.',
  node: 'A step box on the poster. Title lines render inside the box; description bullets go to the right column.',
  lane: 'A parallel stream inside a case, holding its own ordered nodes.',
  table: 'A standalone table with columns and rows.',
  tableRow: 'One row of cells in the table.',
};

/* ── component ─────────────────────────────────────────── */

function finalTitle(f: any): string {
  if (Array.isArray(f && f.title)) return f.title.filter(Boolean).join(' ');
  return (f && f.title) || '';
}

function firstCell(row: any): string {
  const c = Array.isArray(row) && row[0] != null ? String(row[0]) : '';
  return c.length > 40 ? c.slice(0, 40) + '…' : c;
}

/** Display-only decoding. Only an isolated entity can enter the detached
    decoder; stored titles are never parsed as HTML or written back to spec. */
function displayText(value: string | undefined): string {
  const text = String(value || '').replace(/<\/?b>/gi, '');
  let decoder: HTMLTextAreaElement | undefined;
  return text.replace(/&(?:#[xX][\da-fA-F]+|#\d+|[a-zA-Z][a-zA-Z0-9]+);/g, (entity) => {
    decoder = decoder || document.createElement('textarea');
    decoder.innerHTML = entity;
    return decoder.value;
  });
}

function rowText(row: RowSpec): { title: string; detail: string } {
  const label = displayText(row.label);
  let title = displayText(row.preview).trim();
  // Generated step titles often begin with the same ID as the row label.
  // Remove that exact display prefix once, keeping the original title intact.
  if (row.kind === 'node' && title.startsWith(label)) {
    title = title.slice(label.length).replace(/^\s*[:–—-]?\s*/, '');
  } else if (row.kind === 'case' && title.toLowerCase().startsWith(label.toLowerCase() + ':')) {
    title = title.slice(label.length + 1).trim();
  }
  return { title: title || (row.kind === 'node' ? 'Untitled step' : label), detail: label };
}

function parentRowPath(path: string): string | null {
  const segs = path.split('.');
  while (segs.length > 1) {
    segs.pop();
    const prefix = segs.join('.');
    if (pathKind(prefix)) return prefix;
  }
  return null;
}

const KIND_ICON = {
  meta: Heading,
  legend: Layers,
  block: AlignLeft,
  case: Layers,
  final: Flag,
  node: Square,
  lane: Columns2,
  table: Table2,
  tableRow: Rows3,
};

/** Wrap every case-insensitive occurrence of `q` in `text` with a
    <mark>. Returns the raw string when there is no query (no extra
    nodes, no layout shift). */
function highlight(text: string, q: string): React.ReactNode {
  if (!q || !text) return text;
  const lower = text.toLowerCase();
  const ql = q.toLowerCase();
  if (!lower.includes(ql)) return text;
  const out: React.ReactNode[] = [];
  let i = 0;
  let k = 0;
  while (i < text.length) {
    const idx = lower.indexOf(ql, i);
    if (idx === -1) {
      out.push(text.slice(i));
      break;
    }
    if (idx > i) out.push(text.slice(i, idx));
    out.push(
      <mark key={k++} className="arch-tree-mark">
        {text.slice(idx, idx + ql.length)}
      </mark>,
    );
    i = idx + ql.length;
  }
  return out;
}

/** Full, open-state-independent traversal of the spec into flat search
    entries (label + preview + node id + case/lane tags → haystack, plus a
    breadcrumb line). Also supplies the element count and group controls. */
function buildSearchEntries(spec: any): SearchEntry[] {
  const out: SearchEntry[] = [];
  const push = (
    path: string,
    kind: Kind,
    label: string,
    preview: string | undefined,
    extra: string,
    more?: Partial<RowSpec>,
  ) => {
    const hay = displayText(
      label + ' ' + (preview || '') + ' ' + extra
    )
      .toLowerCase()
      .trim();
    const cs = crumbSegments(spec, path);
    const crumb = cs
      .slice(0, -1)
      .map((c) => displayText(c.label))
      .join(' › ');
    out.push({ path, depth: 0, kind, label, preview, hay, crumb, ...more });
  };

  if (spec.meta) {
    push('meta', 'meta', 'Poster header', spec.meta.title || undefined, '');
  }

  if (spec.legend) {
    push('legend', 'legend', 'Legend', undefined, '');
    const blocks = Array.isArray(spec.legend.blocks) ? spec.legend.blocks : [];
    blocks.forEach((b: any, i: number) => {
      push(
        'legend.blocks.' + i,
        'block',
        'Legend block',
        b && b.title ? b.title : undefined,
        '',
      );
    });
  }

  const cases = Array.isArray(spec.cases) ? spec.cases : [];
  cases.forEach((c: any, ci: number) => {
    const cp = 'cases.' + ci;
    push(cp, 'case', 'Case ' + (ci + 1), c.title || undefined, 'case ' + (ci + 1), {
      colorToken: c.color,
    });
    const nodes = Array.isArray(c.nodes) ? c.nodes : [];
    nodes.forEach((n: any, ni: number) => {
      push(
        cp + '.nodes.' + ni,
        'node',
        '[' + (n.id || ni + 1) + ']',
        nodeTitlePreview(n, ni),
        (n.id || '') + ' ' + (n.type || 'box'),
        { nodeType: n.type || 'box' },
      );
    });
    const lanes = Array.isArray(c.lanes) ? c.lanes : [];
    lanes.forEach((l: any, li: number) => {
      const lp = cp + '.lanes.' + li;
      push(lp, 'lane', 'Lane ' + (l.tag || li + 1), l.title || undefined,
        (l.tag || '') + ' ' + (l.role || 'stream'),
        {
          colorToken: l.color,
          badge: (l.role || 'stream') === 'exception' ? 'exception' : undefined,
        });
      const lnodes = Array.isArray(l.nodes) ? l.nodes : [];
      lnodes.forEach((n: any, ni: number) => {
        push(
          lp + '.nodes.' + ni,
          'node',
          '[' + (n.id || ni + 1) + ']',
          nodeTitlePreview(n, ni),
          (n.id || '') + ' ' + (n.type || 'box'),
          { nodeType: n.type || 'box' },
        );
      });
    });
    if (c.final) {
      push(cp + '.final', 'final', 'Final block', finalTitle(c.final) || undefined, '');
    }
  });

  const tables = Array.isArray(spec.tables) ? spec.tables : [];
  tables.forEach((t: any, ti: number) => {
    const tp = 'tables.' + ti;
    push(tp, 'table', 'Table', t.title || undefined, '');
    const trows = Array.isArray(t.rows) ? t.rows : [];
    trows.forEach((r: any, ri: number) => {
      push(tp + '.rows.' + ri, 'tableRow', 'Row ' + (ri + 1), firstCell(r) || undefined, '');
    });
  });

  return out;
}

function buildVisibleRows(spec: any, isOpen: (path: string) => boolean): RowSpec[] {
  // ── normal mode: the collapsible outline ──
  const rows: RowSpec[] = [];

  // Poster header
  rows.push({
    path: 'meta',
    depth: 0,
    kind: 'meta',
    label: 'Poster header',
    preview: spec.meta && spec.meta.title ? spec.meta.title : undefined,
  });

  // Legend + its bullet blocks
  if (spec.legend) {
    const blocks = Array.isArray(spec.legend.blocks) ? spec.legend.blocks : [];
    rows.push({
      path: 'legend',
      depth: 0,
      kind: 'legend',
      label: 'Legend',
      hasChildren: blocks.length > 0,
    });
    if (isOpen('legend')) {
      blocks.forEach((b: any, i: number) => {
        rows.push({
          path: 'legend.blocks.' + i,
          depth: 1,
          kind: 'block',
          label: 'Legend block',
          preview: b && b.title ? b.title : undefined,
        });
      });
    }
  }

  // Cases → pre-lane nodes, lanes (→ nodes), final block
  const cases = Array.isArray(spec.cases) ? spec.cases : [];
  cases.forEach((c: any, ci: number) => {
    const cp = 'cases.' + ci;
    const nodes = Array.isArray(c.nodes) ? c.nodes : [];
    const lanes = Array.isArray(c.lanes) ? c.lanes : [];
    rows.push({
      path: cp,
      depth: 0,
      kind: 'case',
      label: 'Case ' + (ci + 1),
      preview: c.title || undefined,
      colorToken: c.color,
      hasChildren: nodes.length > 0 || lanes.length > 0 || !!c.final,
    });
    if (!isOpen(cp)) return;
    nodes.forEach((n: any, ni: number) => {
      rows.push({
        path: cp + '.nodes.' + ni,
        depth: 1,
        kind: 'node',
        label: '[' + (n.id || ni + 1) + ']',
        preview: nodeTitlePreview(n, ni),
        nodeType: n.type || 'box',
      });
    });
    lanes.forEach((l: any, li: number) => {
      const lp = cp + '.lanes.' + li;
      const lnodes = Array.isArray(l.nodes) ? l.nodes : [];
      rows.push({
        path: lp,
        depth: 1,
        kind: 'lane',
        label: 'Lane ' + (l.tag || li + 1),
        preview: l.title || undefined,
        colorToken: l.color,
        badge: (l.role || 'stream') === 'exception' ? 'exception' : undefined,
        hasChildren: lnodes.length > 0,
      });
      if (!isOpen(lp)) return;
      lnodes.forEach((n: any, ni: number) => {
        rows.push({
          path: lp + '.nodes.' + ni,
          depth: 2,
          kind: 'node',
          label: '[' + (n.id || ni + 1) + ']',
          preview: nodeTitlePreview(n, ni),
          nodeType: n.type || 'box',
        });
      });
    });
    if (c.final) {
      rows.push({
        path: cp + '.final',
        depth: 1,
        kind: 'final',
        label: 'Final block',
        preview: finalTitle(c.final) || undefined,
      });
    }
  });

  // Standalone tables → rows
  const tables = Array.isArray(spec.tables) ? spec.tables : [];
  tables.forEach((t: any, ti: number) => {
    const tp = 'tables.' + ti;
    const trows = Array.isArray(t.rows) ? t.rows : [];
    rows.push({
      path: tp,
      depth: 0,
      kind: 'table',
      label: 'Table',
      preview: t.title || undefined,
      hasChildren: trows.length > 0,
    });
    if (!isOpen(tp)) return;
    trows.forEach((r: any, ri: number) => {
      rows.push({
        path: tp + '.rows.' + ri,
        depth: 1,
        kind: 'tableRow',
        label: 'Row ' + (ri + 1),
        preview: firstCell(r) || undefined,
      });
    });
  });

  return rows;
}

export default function StructureTree({
  workingRef,
  formRev,
  selPath,
  onSelect,
  onHover,
  query,
  setQuery,
  commentAnchorForPath,
}: Props) {
  // Open/closed state survives formRev re-renders (this component is not
  // keyed by formRev, so the ref persists). A missing entry means open.
  const openRef = useRef<Record<string, boolean>>({});
  const [, force] = useReducer((x: number) => x + 1, 0);
  // Live DOM handles for every rendered row, so revealNow can scroll +
  // pulse the reveal target once its ancestors have expanded.
  const rowRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const flashTimer = useRef<number | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const [focusedPath, setFocusedPath] = useState<string | null>(selPath);

  const isOpen = (p: string) => openRef.current[p] !== false;
  const toggle = (p: string) => {
    openRef.current[p] = !isOpen(p);
    force();
  };

  // The tree unmounts when a row click swaps the panel to the edit form —
  // without this cleanup the last-hovered poster group would keep its
  // .arch-hover glow forever.
  useEffect(() => () => onHover(null), [onHover]);

  // Reveal the current selection: auto-expand every collapsed ancestor
  // so its row is actually rendered (and can then scroll into view).
  useEffect(() => {
    if (!selPath) return;
    const segs = selPath.split('.');
    let prefix = '';
    let changed = false;
    for (let i = 0; i < segs.length; i++) {
      prefix = i === 0 ? segs[i] : prefix + '.' + segs[i];
      if (pathKind(prefix) && openRef.current[prefix] === false) {
        openRef.current[prefix] = true;
        changed = true;
      }
    }
    if (changed) force();
  }, [selPath]);

  // Scroll the reveal-target row into view (smooth) and pulse it once
  // (.arch-tree-flash, ~900ms). rAF-retries because the row may only
  // exist after the expand effect above forces a re-render. (DESIGN9 §1)
  const revealNow = useCallback(() => {
    if (!selPath || query) return; // never while the flat result list is up
    const target = selPath;
    let tries = 8;
    const run = () => {
      const el = rowRefs.current[target];
      if (!el) {
        if (tries-- > 0) window.requestAnimationFrame(run);
        return;
      }
      el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      el.classList.remove('arch-tree-flash');
      // force reflow so the pulse restarts even on the same row
      void el.getBoundingClientRect();
      el.classList.add('arch-tree-flash');
      if (flashTimer.current != null) window.clearTimeout(flashTimer.current);
      flashTimer.current = window.setTimeout(() => {
        el.classList.remove('arch-tree-flash');
        flashTimer.current = null;
      }, 950);
    };
    window.requestAnimationFrame(run);
  }, [selPath, query]);

  // Fire on mount and whenever the reveal target changes (revealNow's
  // identity is keyed by selPath), i.e. returning to the tree via
  // '◀ Structure' with a live selection reveals + pulses that row.
  useEffect(() => {
    revealNow();
  }, [revealNow]);

  // Clearing the search ('✕' / Esc) drops back to the normal tree with
  // the selection revealed (rule 1). Only fire on a real non-empty→empty
  // transition so a selPath change doesn't double-pulse.
  const prevQuery = useRef(query);
  useEffect(() => {
    if (prevQuery.current && !query) revealNow();
    prevQuery.current = query;
  }, [query, revealNow]);

  useEffect(
    () => () => {
      if (flashTimer.current != null) window.clearTimeout(flashTimer.current);
    },
    [],
  );

  const spec = workingRef.current;
  const searchQuery = query.trim();
  const entries = spec ? buildSearchEntries(spec) : [];
  const rows = spec ? buildVisibleRows(spec, isOpen) : [];
  const hits = searchQuery
    ? entries.filter((entry) => entry.hay.includes(searchQuery.toLowerCase()))
    : [];
  const visibleRows: RowSpec[] = searchQuery ? hits : rows;
  const tabPath = visibleRows.find((row) => row.path === focusedPath)?.path
    || visibleRows.find((row) => row.path === selPath)?.path
    || visibleRows[0]?.path;
  const groupPaths = entries
    .filter((row) => ['legend', 'case', 'lane', 'table'].includes(row.kind))
    .map((row) => row.path);
  const allExpanded = groupPaths.every(isOpen);

  const focusRow = (path: string) => {
    setFocusedPath(path);
    rowRefs.current[path]?.focus();
  };

  const onRowKeyDown = (event: React.KeyboardEvent<HTMLDivElement>, row: RowSpec) => {
    if (event.target !== event.currentTarget) return;
    const index = visibleRows.findIndex((item) => item.path === row.path);
    let nextPath: string | undefined;
    switch (event.key) {
      case 'ArrowDown':
        nextPath = visibleRows[Math.min(index + 1, visibleRows.length - 1)]?.path;
        break;
      case 'ArrowUp':
        nextPath = visibleRows[Math.max(index - 1, 0)]?.path;
        break;
      case 'Home':
        nextPath = visibleRows[0]?.path;
        break;
      case 'End':
        nextPath = visibleRows[visibleRows.length - 1]?.path;
        break;
      case 'ArrowRight':
        if (searchQuery) return;
        if (row.hasChildren && !isOpen(row.path)) toggle(row.path);
        else if (row.hasChildren) nextPath = visibleRows[index + 1]?.path;
        break;
      case 'ArrowLeft':
        if (searchQuery) return;
        if (row.hasChildren && isOpen(row.path)) toggle(row.path);
        else nextPath = parentRowPath(row.path) || undefined;
        break;
      case 'Enter':
      case ' ':
        onSelect(row.path);
        break;
      case 'Escape':
        if (!searchQuery) return;
        setQuery('');
        searchRef.current?.focus();
        break;
      default:
        return;
    }
    event.preventDefault();
    event.stopPropagation();
    if (nextPath) focusRow(nextPath);
  };

  const rowInner = (row: RowSpec) => {
    const { title, detail } = rowText(row);
    const Icon = row.nodeType === 'decision' ? Diamond
      : row.nodeType === 'fork' ? GitFork : KIND_ICON[row.kind];
    const trailingId = row.kind === 'node' || row.kind === 'tableRow';
    const iconTitle = row.nodeType
      ? row.nodeType.charAt(0).toUpperCase() + row.nodeType.slice(1)
      : detail;
    const commentAnchor = commentAnchorForPath?.(row.path);
    return (
      <>
        <span
          className={'arch-tree-icon' + (row.nodeType ? ' arch-tree-icon-' + row.nodeType : '')}
          style={row.colorToken ? { color: PALETTE[row.colorToken] || 'var(--muted)' } : undefined}
          title={iconTitle}
          aria-hidden="true"
        >
          <Icon size={15} strokeWidth={1.65} />
        </span>
        <span className="arch-tree-copy">
          <span className="arch-tree-title">{highlight(title, searchQuery)}</span>
          {!trailingId && title !== detail ? (
            <span className="arch-tree-caption">{highlight(detail, searchQuery)}</span>
          ) : null}
        </span>
        {trailingId ? <span className="arch-tree-id">{highlight(detail, searchQuery)}</span> : null}
        {row.badge ? <span className="arch-tree-badge">{row.badge}</span> : null}
        {commentAnchor ? <span className="arch-tree-comment" {...commentBlockAttributes(commentAnchor)}><CommentBlockButton anchor={commentAnchor} /></span> : null}
      </>
    );
  };

  const searchBox = (
    <div className="arch-tree-toolbar">
      <div className="arch-tree-search">
        <Search className="arch-tree-search-icon" size={15} aria-hidden="true" />
        <input
          ref={searchRef}
          type="text"
          className="arch-tree-search-input"
          placeholder="Find a block…"
          value={query}
          aria-label="Search blocks"
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              // Keep the editor open while clearing its local search.
              event.preventDefault();
              event.stopPropagation();
              setQuery('');
            } else if (event.key === 'ArrowDown' && visibleRows[0]) {
              event.preventDefault();
              event.stopPropagation();
              focusRow(visibleRows[0].path);
            }
          }}
        />
        {query ? (
          <button
            type="button"
            className="arch-tree-search-clear"
            title="Clear search"
            aria-label="Clear search"
            onClick={() => { setQuery(''); searchRef.current?.focus(); }}
          >
            <X size={14} aria-hidden="true" />
          </button>
        ) : null}
      </div>
      {spec ? (
        <div className="arch-tree-toolbar-meta">
          <span className="arch-tree-count" role="status" aria-live="polite" aria-atomic="true">
            {searchQuery
              ? `${hits.length} ${hits.length === 1 ? 'result' : 'results'}`
              : `${entries.length} ${entries.length === 1 ? 'element' : 'elements'}`}
          </span>
          {!searchQuery && groupPaths.length > 0 ? (
            <button
              type="button"
              className="arch-tree-expand-all"
              onClick={() => {
                groupPaths.forEach((path) => { openRef.current[path] = !allExpanded; });
                force();
              }}
              title={allExpanded ? 'Collapse all groups' : 'Expand all groups'}
            >
              {allExpanded ? <ChevronsDownUp size={13} aria-hidden="true" /> : <ChevronsUpDown size={13} aria-hidden="true" />}
              {allExpanded ? 'Collapse all' : 'Expand all'}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );

  if (!spec) {
    return (
      <div className="arch-tree-panel">
        {searchBox}
        <div className="arch-empty">No structure yet — generate the poster first.</div>
      </div>
    );
  }

  return (
    <div className="arch-tree-panel">
      {searchBox}
      {searchQuery && hits.length === 0 ? (
        <div className="arch-tree-noresults">
          <Search size={22} strokeWidth={1.5} aria-hidden="true" />
          <strong>No matching blocks</strong>
          <span>Try a title, step ID or lane name.</span>
          <button type="button" className="arch-tree-reset" onClick={() => { setQuery(''); searchRef.current?.focus(); }}>
            Clear search
          </button>
        </div>
      ) : (
        <div
          className={'arch-tree' + (searchQuery ? ' arch-tree-results' : '')}
          role={searchQuery ? 'listbox' : 'tree'}
          aria-label={searchQuery ? 'Structure search results' : 'Architecture structure'}
        >
          {visibleRows.map((row) => {
            const selected = row.path === selPath;
            const open = isOpen(row.path);
            const { title, detail } = rowText(row);
            const parent = parentRowPath(row.path);
            const siblings = rows.filter((item) => parentRowPath(item.path) === parent);
            const crumb = (row as SearchEntry).crumb;
            const accessibleLabel = [title, title !== detail ? detail : '', row.nodeType, row.badge].filter(Boolean).join(', ');
            return (
              <div
                key={row.path}
                className={(searchQuery ? 'arch-tree-result' : 'arch-tree-row')
                  + (row.hasChildren ? ' arch-tree-group' : '') + (selected ? ' selected' : '')}
                style={searchQuery ? undefined : { paddingLeft: 4 + row.depth * 14 }}
                role={searchQuery ? 'option' : 'treeitem'}
                tabIndex={row.path === tabPath ? 0 : -1}
                aria-label={accessibleLabel}
                aria-selected={selected}
                aria-expanded={!searchQuery && row.hasChildren ? open : undefined}
                aria-level={searchQuery ? undefined : row.depth + 1}
                aria-posinset={searchQuery ? undefined : siblings.findIndex((item) => item.path === row.path) + 1}
                aria-setsize={searchQuery ? undefined : siblings.length}
                title={accessibleLabel}
                onMouseEnter={() => onHover(row.path)}
                onMouseLeave={() => onHover(null)}
                onFocus={() => { setFocusedPath(row.path); onHover(row.path); }}
                onBlur={() => onHover(null)}
                onClick={() => onSelect(row.path)}
                onKeyDown={(event) => onRowKeyDown(event, row)}
                ref={(element) => { rowRefs.current[row.path] = element; }}
              >
                {searchQuery ? (
                  <>
                    <div className="arch-tree-row arch-tree-result-row">{rowInner(row)}</div>
                    {crumb ? <div className="arch-tree-crumbline">{crumb}</div> : null}
                  </>
                ) : (
                  <>
                    {row.hasChildren ? (
                      <button
                        className="arch-tree-chev"
                        type="button"
                        tabIndex={-1}
                        aria-label={`${open ? 'Collapse' : 'Expand'} ${detail}`}
                        onClick={(event) => {
                          event.stopPropagation();
                          toggle(row.path);
                          focusRow(row.path);
                        }}
                      >
                        {open ? <ChevronDown size={13} aria-hidden="true" /> : <ChevronRight size={13} aria-hidden="true" />}
                      </button>
                    ) : <span className="arch-tree-chev-spacer" aria-hidden="true" />}
                    {rowInner(row)}
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
